import { Link } from "react-router-dom";
import AnimateIn from "@/components/AnimateIn";
import TypewriterHeading from "@/components/TypewriterHeading";
import WCButton from "@/components/WCButton";
import ProductCard from "@/components/ProductCard";
import DropCard from "@/components/DropCard";
import StatusBadge, { statusActionLabel } from "@/components/StatusBadge";
import EmailSignup from "@/components/EmailSignup";
import { useShop } from "@/context/ShopContext";
import {
  drops,
  heroImage,
  storySectionImages,
  dropTeaserImage,
} from "@/data/mockData";

// Curated Core Collection — shown on the homepage. Ordered by handle so Shopify's
// default ordering can't put a hoodie where we want a tee, etc.
const CORE_HANDLES = [
  "not-your-perception-tee",
  "question-everything-tee",
  "they-got-it-wrong-tee",
  "essential-heavyweight-cotton-t-shirt", // X-Question Everything Tee
];

const featuredDrop = drops[0];

export default function Home() {
  const { products } = useShop();
  const coreProducts = CORE_HANDLES.map((h) =>
    products.find((p) => (p.handle || p.slug) === h)
  ).filter(Boolean);
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative w-full h-screen overflow-hidden"
        data-testid="home-hero"
      >
        <img
          src={heroImage}
          alt="WrongConception"
          className="wc-image absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/60" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <div className="max-w-5xl w-full">
            <TypewriterHeading
              text="EVERYONE HAS THE WRONG CONCEPTION."
              speed={80}
              className="text-5xl md:text-7xl lg:text-8xl tracking-[0.06em]"
              testid="hero-typewriter"
            />
          </div>
          <AnimateIn variant="fade" delay={3600}>
            <p className="text-white/80 text-lg md:text-xl mt-10 max-w-2xl">
              Streetwear for the ones who finally started questioning.
            </p>
          </AnimateIn>
          <AnimateIn variant="fade" delay={4000}>
            <div className="flex flex-col md:flex-row gap-5 mt-10">
              <WCButton as="link" to="/shop" data-testid="hero-shop-now">
                Shop Now
              </WCButton>
              <WCButton as="link" to="/drops" data-testid="hero-view-drop">
                View The Drop
              </WCButton>
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* BRAND STATEMENT */}
      <section className="bg-black px-6 py-32 md:py-48" data-testid="brand-statement">
        <div className="max-w-5xl mx-auto text-center space-y-12">
          <AnimateIn variant="fade">
            <h2 className="wc-heading text-white text-4xl md:text-6xl lg:text-7xl tracking-widest">
              WE DO NOT MAKE CLOTHES FOR EVERYONE.
            </h2>
          </AnimateIn>
          <AnimateIn variant="fade" delay={300}>
            <h2 className="wc-heading text-white text-4xl md:text-6xl lg:text-7xl tracking-widest">
              WE MAKE THEM FOR THE ONES WHO STOPPED ACCEPTING WHAT THEY WERE TOLD.
            </h2>
          </AnimateIn>
          <AnimateIn variant="fade" delay={600}>
            <p className="text-white/60 text-sm wc-label pt-8">
              WRONG CONCEPTION
            </p>
          </AnimateIn>
        </div>
      </section>

      {/* CORE COLLECTION */}
      <section className="bg-black px-6 md:px-12 py-24" data-testid="core-collection">
        <div className="max-w-7xl mx-auto">
          <AnimateIn variant="fade">
            <div className="wc-label text-xs text-white/50">ALWAYS AVAILABLE</div>
            <h2 className="wc-heading text-white text-5xl md:text-7xl mt-3 tracking-widest">
              CORE COLLECTION
            </h2>
          </AnimateIn>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8 mt-14">
            {coreProducts.slice(0, 4).map((p, i) => (
              <AnimateIn key={p.handle || p.id} variant="fade" delay={i * 120}>
                <ProductCard product={p} />
              </AnimateIn>
            ))}
          </div>
          <div className="text-center mt-16">
            <WCButton as="link" to="/shop" data-testid="core-view-all">
              View All
            </WCButton>
          </div>
        </div>
      </section>

      {/* FEATURED DROP TEASER */}
      <section className="bg-black" data-testid="drop-teaser">
        <div className="grid md:grid-cols-2">
          <AnimateIn variant="slide-left" className="relative min-h-[500px] md:min-h-[700px]">
            <img
              src={dropTeaserImage}
              alt={featuredDrop.name}
              className="wc-image absolute inset-0 w-full h-full object-cover"
            />
          </AnimateIn>
          <AnimateIn
            variant="slide-right"
            className="border-l border-white/20 bg-black px-8 md:px-16 py-16 md:py-24 flex flex-col justify-center"
          >
            <div className="wc-label text-[11px] text-white/50">
              {featuredDrop.category.toUpperCase()}
            </div>
            <div className="wc-label text-[11px] text-white/50 mt-2">
              {featuredDrop.volume.toUpperCase()}
            </div>
            <h3 className="wc-heading text-white text-5xl md:text-7xl tracking-widest mt-6">
              {featuredDrop.name}
            </h3>
            <p className="text-white/70 text-base md:text-lg mt-6 max-w-md leading-relaxed">
              {featuredDrop.description}
            </p>
            <div className="mt-6">
              <StatusBadge status={featuredDrop.status} />
            </div>
            <div className="mt-8">
              <WCButton
                as="link"
                to={`/${featuredDrop.slug}`}
                data-testid="featured-drop-cta"
              >
                {statusActionLabel(featuredDrop.status)}
              </WCButton>
            </div>
          </AnimateIn>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="bg-black px-6 md:px-12 py-32" data-testid="three-pillars">
        <div className="max-w-7xl mx-auto">
          <AnimateIn variant="fade" className="text-center">
            <h2 className="wc-heading text-white text-5xl md:text-7xl tracking-widest">
              THE WRONG CONCEPTION
            </h2>
            <p className="text-white/60 mt-4 text-base md:text-lg">Everything we challenge.</p>
          </AnimateIn>
          <div className="grid md:grid-cols-3 gap-12 md:gap-6 mt-20">
            {[
              {
                num: "01",
                title: "YOURSELF",
                body: "The conception you carry about yourself was built by someone else before you were old enough to build your own.",
                link: "LEARN MORE",
                to: "/our-story",
                variant: "slide-left",
              },
              {
                num: "02",
                title: "THE WORLD",
                body: "Every place you were told to fear or dismiss has a story the world got completely wrong. We go there to find it.",
                link: "VIEW DROPS",
                to: "/drops",
                variant: "fade",
              },
              {
                num: "03",
                title: "THE SYSTEM",
                body: "The system was not designed for your freedom. It was designed for your compliance. Those are very different things.",
                link: "OUR STORY",
                to: "/our-story",
                variant: "slide-right",
              },
            ].map((p) => (
              <AnimateIn
                key={p.num}
                variant={p.variant}
                className="relative overflow-hidden px-6 py-14 border border-white/10"
                data-testid={`pillar-${p.num}`}
              >
                <div
                  className="wc-heading absolute -top-10 -right-4 text-[16rem] md:text-[20rem] leading-none text-white/5 select-none pointer-events-none"
                  aria-hidden="true"
                >
                  {p.num}
                </div>
                <div className="relative z-10">
                  <h3 className="wc-heading text-white text-4xl md:text-5xl tracking-widest">
                    {p.title}
                  </h3>
                  <p className="text-white/70 text-base mt-6 leading-relaxed">{p.body}</p>
                  <Link
                    to={p.to}
                    className="wc-label text-xs text-white mt-10 inline-block border-b border-white pb-1"
                    data-testid={`pillar-link-${p.num}`}
                  >
                    {p.link} →
                  </Link>
                </div>
              </AnimateIn>
            ))}
          </div>
        </div>
      </section>

      {/* STORY A - QUESTION EVERYTHING */}
      <StorySection
        image={storySectionImages.questionEverything}
        imageSide="left"
        label="CORE COLLECTION"
        heading="QUESTION EVERYTHING"
        body="Not everything you have been told is true. This piece exists for the ones who never stopped asking why. Built heavy. Built to last. Built for those who think different."
        ctaTo="/shop/question-everything-sweatshirt"
        testid="story-a"
      />

      {/* STORY B - THEY GOT IT WRONG */}
      <StorySection
        image={storySectionImages.theyGotItWrong}
        imageSide="right"
        label="CORE COLLECTION"
        heading="THEY GOT IT WRONG"
        body="They got it wrong about you. About where you are from. About what you are capable of. Their conception of you only has power while you live inside it. Step outside it."
        ctaTo="/shop/they-got-it-wrong-tee"
        testid="story-b"
      />

      {/* STORY C - NOT YOUR PERCEPTION */}
      <StorySection
        image={storySectionImages.notYourPerception}
        imageSide="left"
        label="CORE COLLECTION"
        heading="NOT YOUR PERCEPTION"
        body="The way people see you is not who you are. Someone built a perception before they ever knew you and passed it around like it was fact. This is for the ones who stopped living inside it."
        ctaTo="/shop/not-your-perception-sweatshirt"
        testid="story-c"
      />

      {/* DROPS ROADMAP */}
      <section className="bg-black px-6 md:px-12 py-32" data-testid="drops-roadmap">
        <div className="max-w-7xl mx-auto">
          <AnimateIn variant="fade" className="text-center">
            <h2 className="wc-heading text-white text-4xl md:text-6xl tracking-widest max-w-3xl mx-auto">
              THE DROPS. EVERY ONE TELLS A STORY.
            </h2>
            <p className="text-white/60 mt-5 text-base md:text-lg max-w-2xl mx-auto">
              No guaranteed restock. Once it is gone, it may not come back.
            </p>
          </AnimateIn>
          <div className="grid md:grid-cols-3 gap-8 md:gap-6 mt-16">
            {drops.slice(0, 3).map((d, i) => (
              <AnimateIn key={d.id} variant="fade" delay={i * 120}>
                <DropCard drop={d} />
              </AnimateIn>
            ))}
          </div>
          <div className="text-center mt-16">
            <WCButton as="link" to="/drops" data-testid="roadmap-view-all">
              View All Drops
            </WCButton>
            <p className="text-white/40 italic text-sm mt-6">
              Every drop tells a story. Every story challenges a wrong conception.
            </p>
          </div>
        </div>
      </section>

      {/* EMAIL SIGNUP */}
      <EmailSignup source="homepage" />
    </div>
  );
}

const StorySection = ({ image, imageSide, label, heading, body, ctaTo, testid }) => {
  const imgLeft = imageSide === "left";
  return (
    <section className="bg-black" data-testid={testid}>
      <div className={`grid md:grid-cols-2 ${imgLeft ? "" : "md:[&>*:first-child]:order-2"}`}>
        <AnimateIn
          variant={imgLeft ? "slide-left" : "slide-right"}
          className="relative min-h-[500px] md:min-h-[650px]"
        >
          <img src={image} alt={heading} className="wc-image absolute inset-0 w-full h-full object-cover" />
        </AnimateIn>
        <AnimateIn
          variant={imgLeft ? "slide-right" : "slide-left"}
          className="border border-white/20 bg-black px-8 md:px-16 py-16 md:py-24 flex flex-col justify-center"
        >
          <div className="wc-label text-[11px] text-white/50">{label}</div>
          <h3 className="wc-heading text-white text-5xl md:text-7xl tracking-widest mt-6">
            {heading}
          </h3>
          <p className="text-white/75 text-base md:text-lg mt-8 max-w-md leading-relaxed">
            {body}
          </p>
          <div className="mt-10">
            <WCButton as="link" to={ctaTo} data-testid={`${testid}-cta`}>
              Shop Now
            </WCButton>
          </div>
        </AnimateIn>
      </div>
    </section>
  );
};
